import { last, uniqBy } from "lodash";
import moment from "moment";
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { MeetingsOverviewMeetingFragment } from "types/graphql-schema";

import { currentUserVar } from "@cache/cache";
import Loading from "@components/loading/loading";
import { classNames } from "@helpers/css";

import MeetingOverviewPage from "./meeting-overview-page";
import { MeetingType } from "./meetings-overview-constants";
import OverviewMeetingList from "./overview-meeting-list";

type DateRange = { start: string; end: string };

type PageConfig = {
  id: string;
  range: DateRange;
  direction: "future" | "past";
};

type PageResult = {
  meetings: MeetingsOverviewMeetingFragment[];
  loading: boolean;
};

const getInitialRange = (selectedDay: string, isCompact: boolean): DateRange =>
  isCompact
    ? {
        start: moment(selectedDay).startOf("day").format(),
        end: moment(selectedDay).endOf("day").format(),
      }
    : {
        start: moment(selectedDay).startOf("isoWeek").format(),
        end: moment(selectedDay).endOf("isoWeek").format(),
      };

const MeetingOverview = ({
  selectedDay,
  meetingListClassName,
  onClickMeetingLink,
  forceTodayScroll,
  compact: isCompact = false,
  participantFilter = null,
  meetingTypeFilter = MeetingType.ALL,
}: {
  forceTodayScroll: number;
  selectedDay: string;
  meetingListClassName?: string;
  onClickMeetingLink?: (meetingId: number, meetingGroupId: number) => void;
  compact?: boolean;
  participantFilter?: number | null;
  meetingTypeFilter?: MeetingType;
}) => {
  const selectedUser = currentUserVar();
  const scrollTargetRef = useRef<HTMLDivElement>(null);
  const todayRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const minimum = meetingTypeFilter === MeetingType.ONE_ON_ONE ? 5 : 10;

  const participants = useMemo(
    () => (participantFilter ? [participantFilter] : [selectedUser.id]),
    [participantFilter, selectedUser.id],
  );
  const isOneonone =
    meetingTypeFilter === MeetingType.ALL
      ? undefined
      : meetingTypeFilter === MeetingType.ONE_ON_ONE;

  const initialRange = getInitialRange(selectedDay, isCompact);

  // --- Page management ---
  const [pages, setPages] = useState<PageConfig[]>(() => [
    { id: "initial", range: initialRange, direction: "future" },
  ]);
  const [pageResults, setPageResults] = useState<Record<string, PageResult>>(
    {},
  );
  const [loadMore, setLoadMore] = useState<"past" | "future" | null>(null);
  const [fillerHeight, setFillerHeight] = useState(0);
  const autoLoadedRef = useRef(false);

  // Reset pages when dependencies change
  useEffect(() => {
    const range = getInitialRange(selectedDay, isCompact);
    const id = `initial-${Date.now()}`;
    setPages([{ id, range, direction: "future" }]);
    setPageResults({});
    setLoadMore(null);
    autoLoadedRef.current = false;
  }, [selectedDay, isCompact, participantFilter, meetingTypeFilter]);

  // Handle data from page components
  const handlePageData = useCallback(
    (
      pageId: string,
      meetings: MeetingsOverviewMeetingFragment[],
      loading: boolean,
    ) => {
      setPageResults((prev) => {
        if (
          prev[pageId]?.meetings === meetings &&
          prev[pageId]?.loading === loading
        ) {
          return prev;
        }
        return { ...prev, [pageId]: { meetings, loading } };
      });
    },
    [],
  );

  // Combine all meetings from all pages
  const allMeetings = useMemo(() => {
    const combined = pages.flatMap((p) => pageResults[p.id]?.meetings || []);
    return uniqBy(combined, "id").sort((a, b) =>
      moment(a.startDatetime).diff(moment(b.startDatetime)),
    );
  }, [pages, pageResults]);

  // Compute the displayed date range
  const meetingDisplayedRange = useMemo(() => {
    if (isCompact) {
      return {
        start: moment(selectedDay).startOf("day").format(),
        end: moment(selectedDay).endOf("day").format(),
      };
    }

    const range = getInitialRange(selectedDay, false);
    let start = moment(range.start);
    let end = moment(range.end);

    // Include all page ranges
    for (const page of pages) {
      start = moment.min(start, moment(page.range.start));
      end = moment.max(end, moment(page.range.end));
    }

    // Also include meetings that might extend beyond page ranges
    // (due to minimumDateRange expansion by the backend)
    for (const meeting of allMeetings) {
      if (meeting.startDatetime) {
        start = moment.min(start, moment(meeting.startDatetime).startOf("day"));
      }
      if (meeting.endDatetime) {
        end = moment.max(end, moment(meeting.endDatetime).endOf("day"));
      }
    }

    return { start: start.format(), end: end.format() };
  }, [isCompact, selectedDay, pages, allMeetings]);

  // Store displayedRange in a ref for load-more calculations
  const displayedRangeRef = useRef(meetingDisplayedRange);
  displayedRangeRef.current = meetingDisplayedRange;

  // Loading state — only show full loading UI when we have no meetings at all yet
  const isInitialLoading =
    allMeetings.length === 0 &&
    pages.some((p) => {
      const result = pageResults[p.id];
      return !result || result.loading;
    });

  // Has-more logic
  const hasMoreInPast = !isCompact && allMeetings.length > 0;
  const hasMoreInFuture = useMemo(() => {
    if (isCompact) return false;
    const lastPage = pages[pages.length - 1];
    const result = pageResults[lastPage?.id];
    if (!result || result.loading) return false;
    return result.meetings.length >= minimum;
  }, [isCompact, pages, pageResults, minimum]);

  // Load more handler
  const handleSetLoadMore = useCallback(
    (direction: "past" | "future" | null) => {
      if (!direction) return;
      const range = displayedRangeRef.current;
      const newRange: DateRange =
        direction === "past"
          ? {
              start: moment(range.start)
                .subtract(1, "week")
                .startOf("isoWeek")
                .format(),
              end: moment(range.start).subtract(1, "day").endOf("day").format(),
            }
          : {
              start: moment(range.end).add(1, "day").startOf("day").format(),
              end: moment(range.end).add(1, "week").endOf("isoWeek").format(),
            };
      const pageId = `${direction}-${Date.now()}`;
      setLoadMore(direction);
      setPages((prev) => {
        const newPage: PageConfig = {
          id: pageId,
          range: newRange,
          direction,
        };
        return direction === "past" ? [newPage, ...prev] : [...prev, newPage];
      });
    },
    [],
  );

  // Clear loadMore when the new page finishes loading
  useEffect(() => {
    if (!loadMore) return;
    const targetPage = loadMore === "past" ? pages[0] : pages[pages.length - 1];
    const result = pageResults[targetPage?.id];
    if (result && !result.loading) {
      setLoadMore(null);
    }
  }, [loadMore, pages, pageResults]);

  // Auto-load next week on Thu/Fri
  useEffect(() => {
    if (autoLoadedRef.current || isCompact) return;
    const selectedDayIsToday = moment().isSame(selectedDay, "day");
    const shouldFetchNextWeek =
      selectedDayIsToday && moment().isoWeekday() >= 4;
    if (!shouldFetchNextWeek) return;

    const initialPage = pages[0];
    const result = pageResults[initialPage?.id];
    if (!result || result.loading) return;

    const range = getInitialRange(selectedDay, false);
    const lastMeeting = last(result.meetings);
    if (
      !lastMeeting ||
      moment(lastMeeting.endDatetime).isSameOrBefore(range.end, "day")
    ) {
      autoLoadedRef.current = true;
      handleSetLoadMore("future");
    }
  }, [pageResults, pages, isCompact, selectedDay, handleSetLoadMore]);

  // --- Scroll logic (preserved from original) ---

  const pendingScrollRef = useRef<{
    ref: React.RefObject<HTMLDivElement | null>;
    instant: boolean;
  } | null>(null);

  const scrollToRef = (
    ref: React.RefObject<HTMLDivElement | null>,
    instant = false,
  ) => {
    if (ref.current && scrollContainerRef.current) {
      const scrollTop = ref.current.offsetTop;
      scrollContainerRef.current.scrollTo({
        top: scrollTop - 10,
        behavior: instant ? "instant" : "smooth",
      });
    }
  };

  const updateFillerHeight = useCallback(() => {
    const targetEl = scrollTargetRef.current || todayRef.current;
    if (!targetEl || !scrollContainerRef.current) return;
    const container = scrollContainerRef.current;
    const fillerEl = container.querySelector("#filler") as HTMLElement | null;
    const currentFillerHeight = fillerEl ? fillerEl.clientHeight : 0;
    const contentHeight = container.scrollHeight - currentFillerHeight;
    const needed = Math.max(
      0,
      container.clientHeight - (contentHeight - targetEl.offsetTop),
    );
    if (fillerEl) {
      fillerEl.style.minHeight = `${needed}px`;
    }
    setFillerHeight(needed);
    if (pendingScrollRef.current) {
      scrollToRef(
        pendingScrollRef.current.ref,
        pendingScrollRef.current.instant,
      );
    }
  }, []);

  // Scroll to selected day when initial data loads
  const hasScrolledRef = useRef(false);
  useEffect(() => {
    if (isCompact || hasScrolledRef.current) return;
    const initialPage = pages[0];
    const result = pageResults[initialPage?.id];
    if (result && result.meetings.length > 0) {
      hasScrolledRef.current = true;
      pendingScrollRef.current = { ref: scrollTargetRef, instant: true };
      setTimeout(() => {
        pendingScrollRef.current = null;
      }, 500);
    }
  }, [isCompact, pages, pageResults]);

  // Reset scroll tracking when dependencies change
  useEffect(() => {
    hasScrolledRef.current = false;
  }, [selectedDay, isCompact, participantFilter, meetingTypeFilter]);

  useLayoutEffect(() => {
    if (!isCompact) {
      updateFillerHeight();
    }
  }, [allMeetings, meetingDisplayedRange, isCompact, updateFillerHeight]);

  useEffect(() => {
    if (isCompact || !scrollContainerRef.current) return;
    const observer = new ResizeObserver(() => {
      updateFillerHeight();
    });
    observer.observe(scrollContainerRef.current);
    return () => observer.disconnect();
  }, [isCompact, updateFillerHeight]);

  useEffect(() => {
    if (forceTodayScroll > 0) {
      scrollToRef(todayRef);
    }
  }, [forceTodayScroll]);

  // Filter to selected day in compact mode
  const meetingsToDisplay = isCompact
    ? allMeetings.filter((m) =>
        moment(m.startDatetime).isSame(moment(selectedDay), "day"),
      )
    : allMeetings;

  return (
    <div
      className={classNames(
        "@container/overview-meetings",
        isCompact ? "relative" : "h-full",
      )}
    >
      {/* Headless page components that fetch data */}
      {pages.map((page) => (
        <MeetingOverviewPage
          key={page.id}
          pageId={page.id}
          range={page.range}
          direction={page.direction}
          minimum={isCompact ? 0 : minimum}
          participants={participants}
          isOneonone={isOneonone}
          onData={handlePageData}
          pollInterval={isCompact ? 5 * 60 * 1000 : undefined}
        />
      ))}

      <div
        className={classNames(
          isCompact
            ? "px-3 pt-1 overflow-y-auto scrollbar-hidden"
            : "absolute inset-0 overflow-y-scroll scrollbar-hidden pb-12 px-3 pt-4",
          meetingListClassName,
        )}
        ref={scrollContainerRef}
      >
        {isInitialLoading ? (
          <div className="text-gray-500 px-2 flex items-center gap-2 pb-2">
            Loading meetings...
            <Loading size="4" mini />
          </div>
        ) : meetingsToDisplay.length === 0 ? (
          <div className="text-gray-500 px-2 pb-2">No meetings.</div>
        ) : (
          <OverviewMeetingList
            ref={scrollTargetRef}
            meetings={meetingsToDisplay}
            loading={!!loadMore}
            participantFilter={participantFilter}
            meetingTypeFilter={meetingTypeFilter}
            meetingDisplayedRange={
              isCompact
                ? {
                    start: moment(selectedDay).startOf("day").format(),
                    end: moment(selectedDay).endOf("day").format(),
                  }
                : meetingDisplayedRange
            }
            onClickMeetingLink={onClickMeetingLink}
            canLoadMoreInFuture={hasMoreInFuture}
            canLoadMoreInPast={hasMoreInPast}
            loadMore={loadMore}
            onChangeLoadMore={handleSetLoadMore}
            isShowingWeekends={true}
            fillerHeight={fillerHeight}
            compact={isCompact}
            scrollToDay={selectedDay}
            todayRef={todayRef}
          />
        )}
      </div>
    </div>
  );
};

export default React.memo(MeetingOverview);
